import type { ChangeEvent } from 'react';

import { PlusIcon } from 'shared/icons/PlusIcon';
import { MinusIcon } from 'shared/icons/MinusIcon';
import type { NumberRange, RoomWithPeople } from '@/interfaces';
import { ChildAgePicker } from '@/containers/travellersPicker/components/childAgePicker/ChildAgePicker';

export interface RoomsProps {
    room: RoomWithPeople;
    adultLimits: NumberRange;
    addAdult: () => void;
    removeAdult: () => void;
    setAdults: (adults: number) => void;
    childLimits: NumberRange;
    addChild: () => void;
    removeChild: () => void;
    setChildren: (children: number) => void;
    setChildAge: (childIndex: number, age: number) => void;
    roomIndex: number;
    isMobile: boolean;
}

export const HotelRoomItem = ({
    room,
    adultLimits,
    addAdult,
    removeAdult,
    setAdults,
    childLimits,
    addChild,
    removeChild,
    setChildren,
    setChildAge,
    roomIndex,
    isMobile,
}: RoomsProps) => {
    const indexPostfix = roomIndex >= 0 ? `-${roomIndex}` : '';

    function renderMobile() {
        return (
            <>
                <div className="grid u-ma16 u-mt24">
                    <div className="grid__unit u-size1of2 u-left u-mt8 u-truncate">Voksne</div>
                    <div className="grid__unit u-size1of2 u-position-relative">
                        <button
                            type="button"
                            className="transparent u-float-left"
                            onClick={removeAdult}
                            aria-label="Trekk fra én voksen"
                            disabled={room.adults === adultLimits[0]}
                            data-testid={`remove-adult${indexPostfix}`}>
                            <MinusIcon width={48} height={48} className="u-mh8" disabled={room.adults === adultLimits[0]} />
                        </button>
                        <span id="adults_count" data-testid={`adults-count${indexPostfix}`} className="centerAge u-t3">
                            {room.adults}
                        </span>
                        <button
                            type="button"
                            className="transparent u-float-right u-mr0"
                            onClick={addAdult}
                            aria-label="Legg til én voksen"
                            disabled={room.adults === adultLimits[1]}
                            data-testid={`add-adult${indexPostfix}`}>
                            <PlusIcon width={48} height={48} className="u-mh8" disabled={room.adults === adultLimits[1]} />
                        </button>
                    </div>
                </div>
                <div className={`grid u-ma16 u-mt32 ${room.children.length === childLimits[0] ? '' : 'u-pb8'}`}>
                    <div className="grid__unit u-size1of2 u-left u-mt8 u-truncate">Barn (0-17 år)</div>
                    <div className="grid__unit u-size1of2 u-position-relative">
                        <button
                            type="button"
                            className="transparent u-float-left"
                            onClick={removeChild}
                            aria-label="Trekk fra ett barn"
                            disabled={room.children.length === childLimits[0]}
                            data-testid={`remove-child${indexPostfix}`}>
                            <MinusIcon width={48} height={48} className="u-mh8" disabled={room.children.length === childLimits[0]} />
                        </button>
                        <span className="centerAge u-t3" data-testid={`children-count${indexPostfix}`}>
                            {room.children.length}
                        </span>
                        <button
                            type="button"
                            className="transparent u-float-right"
                            onClick={addChild}
                            aria-label="Legg til ett barn"
                            disabled={room.children.length === childLimits[1]}
                            data-testid={`add-child${indexPostfix}`}>
                            <PlusIcon width={48} height={48} className="u-mh8" disabled={room.children.length === childLimits[1]} />
                        </button>
                    </div>
                </div>
                {room.children.map((childAge, childIndex) => (
                    <ChildAgePicker
                        // biome-ignore lint/suspicious/noArrayIndexKey: This existed before biome was added
                        key={`child-${roomIndex}-${childIndex}`}
                        childAge={childAge}
                        roomIndex={roomIndex}
                        childIndex={childIndex}
                        setChildAge={(age: number) => setChildAge(childIndex, age)}
                    />
                ))}
            </>
        );
    }

    function renderDesktop() {
        return (
            <>
                <div className="grid u-pa8 no-text-select" data-testid={`hotelRoomItemAdult${indexPostfix}`}>
                    <div className="grid__unit u-size1of2 u-left u-pl2 u-b1 u-truncate">Voksne</div>
                    <div className="grid__unit u-right u-size1of2 u-position-relative u-pt4">
                        <button
                            type="button"
                            className="transparent u-float-left u-ml0"
                            onClick={removeAdult}
                            aria-label="Trekk fra én voksen"
                            disabled={room.adults === adultLimits[0]}
                            tabIndex={-1}
                            data-testid={`remove-adult${indexPostfix}`}>
                            <MinusIcon width={28} height={28} disabled={room.adults === adultLimits[0]} />
                        </button>

                        <button
                            type="button"
                            className="transparent u-float-right u-mr0"
                            onClick={addAdult}
                            aria-label="Legg til én voksen"
                            tabIndex={-1}
                            disabled={room.adults === adultLimits[1]}
                            data-testid={`add-adult${indexPostfix}`}>
                            <PlusIcon width={28} height={28} disabled={room.adults === adultLimits[1]} />
                        </button>

                        <input
                            id={`passenger-input-adults${indexPostfix}`}
                            type="number"
                            className="passenger-input"
                            data-testid={`adults-count${indexPostfix}`}
                            onFocus={(event) => event.target.select()}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => setAdults(~~event.target.value)}
                            value={room.adults || adultLimits[0]}
                        />
                        <label htmlFor={`passenger-input-adults${indexPostfix}`} className="u-screen-reader-only">
                            Voksne
                        </label>
                    </div>
                </div>
                <div className={`grid u-pa8 no-text-select ${room.children.length === childLimits[0] ? '' : 'u-pb16'}`}>
                    <div style={{ fontWeight: 400 }} className="grid__unit u-size1of2 u-left u-pl2 u-truncate">
                        Barn (0-17 år)
                    </div>
                    <div className="grid__unit u-size1of2 u-position-relative u-pt4">
                        <button
                            type="button"
                            className="transparent u-float-left u-ml0"
                            onClick={removeChild}
                            aria-label="Trekk fra ett barn"
                            disabled={room.children.length === childLimits[0]}
                            tabIndex={-1}
                            data-testid={`remove-child${indexPostfix}`}>
                            <MinusIcon width={28} height={28} disabled={room.children.length === childLimits[0]} />
                        </button>

                        <button
                            type="button"
                            className="transparent u-float-right u-mr0"
                            onClick={addChild}
                            aria-label="Legg til ett barn"
                            tabIndex={-1}
                            disabled={room.children.length === childLimits[1]}
                            data-testid={`add-child${indexPostfix}`}>
                            <PlusIcon width={28} height={28} disabled={room.children.length === childLimits[1]} />
                        </button>

                        <input
                            id={`passenger-input-children${indexPostfix}`}
                            type="number"
                            className="passenger-input"
                            data-testid={`children-count${indexPostfix}`}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => setChildren(~~event.target.value)}
                            onFocus={(event) => event.target.select()}
                            value={room.children.length}
                        />
                        <label htmlFor={`passenger-input-children${indexPostfix}`} className="u-screen-reader-only">
                            Barn (0-17 år)
                        </label>
                    </div>
                </div>
                {room.children.map((childAge, childIndex) => (
                    <ChildAgePicker
                        // biome-ignore lint/suspicious/noArrayIndexKey: This existed before biome was added
                        key={`child-${roomIndex}-${childIndex}`}
                        childAge={childAge}
                        roomIndex={roomIndex}
                        childIndex={childIndex}
                        setChildAge={(age: number) => setChildAge(childIndex, age)}
                    />
                ))}
            </>
        );
    }

    return isMobile ? renderMobile() : renderDesktop();
};
